/**
 * Where something has got to, as a row of chevrons.
 *
 * ⚠️ IT REPLACED A TAB STRIP, AND THE SHAPE IS THE POINT (Josh, 2026-09-04:
 * "these don't really read as a status"). Five words in a row with an underline
 * under one of them is the same control the tables use to switch views, so the
 * one thing it existed to say — where this record has got to — was the one thing
 * it did not say. Chevrons point, and a pointing shape reads as a sequence
 * before a single word is read.
 *
 * ⚠️ COLOUR IS STATE, NOT DECORATION. A different hue per step would be a
 * rainbow that says nothing, and the visual standard rules it out. Three states
 * carry the meaning: passed, here now, not yet — plus green on the last step
 * once it is actually reached, because that is the one that means finished.
 *
 * ⚠️ A COMPONENT, NOT A CLASS TO COPY. There were already two hand-rolled step
 * rails in this app and a third was about to appear. Both survivors of that
 * habit — PageHero and StatCard — got written after the copies had already
 * drifted apart.
 *
 * `onSelect` is optional on purpose: on an Inspector account the steps are real
 * controls because staff genuinely move an account backwards, but on a form
 * wizard they are a position report and clicking one would skip validation.
 */
export interface Step {
  key: string;
  label: string;
}

export function StepBar({
  steps,
  current,
  onSelect,
  disabled = false,
  label = 'Progress',
}: {
  steps: Step[];
  /** The key of the step the record is on. Unknown keys mean "not started". */
  current: string;
  /** Omit to render a read-only position report. */
  onSelect?: (key: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  const at = steps.findIndex((s) => s.key === current);
  const interactive = Boolean(onSelect);

  return (
    <nav className="step-bar" aria-label={label}>
      {steps.map((s, i) => {
        const isNow = i === at;
        const isDone = at > -1 && i < at;
        const cls = `step-chev${isDone ? ' is-done' : ''}${isNow ? ' is-now' : ''}${
          isNow && i === steps.length - 1 ? ' is-final' : ''
        }`;
        const body = (
          <>
            <span>Step {i + 1}</span>
            <b>{s.label}</b>
          </>
        );
        // A div rather than a disabled button when there is nothing to click:
        // a button that does nothing still takes focus and still looks pressable.
        return interactive ? (
          <button
            key={s.key}
            type="button"
            className={cls}
            aria-current={isNow ? 'step' : undefined}
            disabled={disabled}
            onClick={() => onSelect?.(s.key)}
            title={`Move to ${s.label}`}
          >
            {body}
          </button>
        ) : (
          <div key={s.key} className={cls} aria-current={isNow ? 'step' : undefined}>
            {body}
          </div>
        );
      })}
    </nav>
  );
}
